import React, { PropsWithChildren, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Select from '../KitFormComponents/KitSelect';
import { NavLink, AppLink } from './KitNavBar';
import KitNavLink from '../KitNavLink';
import colors from '../../common/theme/colors';
import constants from '../../common/theme/constants';
import { KitFlexCol, KitFlexRowSpaced } from '../KitFlexbox';
import smallLogo from '../../images/ic_cp_round_logo.png';
import { EventName, TranslateFunc } from '../../common/types';
import { onEscape, onFocusOut as onFocusOutAction } from '../../common/events';
import useWindowSize from '../../common/hooks/useWindowSize';

const SlidingMenuTransparentBG = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${constants.zIndex.sliding_menu_bg};
  background-color: ${colors.black}AA;
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
`;

const SlidingMenu = styled.nav<{ isOpen: boolean; height: number }>`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  height: ${({ height }) => `${height}px`};
  background-color: ${({ theme }) => theme.page.header.bg};
  border-right: ${constants.border.width}px solid ${colors.blue_30};
  text-align: left;
  z-index: ${constants.zIndex.sliding_menu};
  transition: transform 0.3s ease-in-out;
  transform: ${({ isOpen }) => (isOpen ? 'translateX(0)' : 'translateX(-100%)')};
  width: 300px;
`;

const SlidingMenuHeader = styled(KitFlexRowSpaced)`
  align-items: center;
  background-color: ${({ theme }) => theme.page.header.bg};
  height: auto;
  padding: ${constants.spacing.m}rem;
  border-bottom: ${constants.border.width}px solid ${colors.blue_30};
`;

const SlidingMenuAppsList = styled(KitFlexCol)`
  background-color: ${colors.blue_30};
  padding: ${constants.spacing.s}rem;
`;

const SlidingMenuBody = styled(KitFlexCol)`
  overflow-y: auto;
  background-color: ${({ theme }) => theme.page.header.bg};
  padding: ${constants.spacing.m}rem;
`;

const StyledHr = styled.hr`
  background-color: ${colors.gray_30};
  width: 100%;
`;

const VerticalNav = styled.nav`
  && {
    flex-direction: column;
    flex-wrap: unset;
    flex: 1;
  }
`;

const NavItem = styled.div`
  transition: opacity 1s;
  white-space: nowrap;
  &:hover {
    background-color: ${({ theme }) => theme.button.link.hover.bg};
  }
`;

const AppTitle = styled.div`
  font-size: ${constants.fontSize.h4}rem;
  padding-left: ${constants.spacing.m}rem;
  padding-right: ${constants.spacing.m}rem;
  white-space: nowrap;
`;

const AppSwitcher = styled(Select)`
  .select2__control {
    background-color: transparent;
    border: none;
  }

  .select2__control:hover {
    border: none;
  }

  &.select2 {
    border: ${constants.border.width}px solid transparent;
  }

  &.select2:focus-within {
    border: ${constants.border.width}px solid ${({ theme }) => theme.focus};
  }
`;

const ListGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding-left: 0;
  margin-bottom: 0;
`;

interface SlidingMenuProps {
  className?: string;
  show: boolean;
  navLinks?: Array<NavLink>;
  activeNavLinkIndex?: number;
  apps?: AppLink[];
  useReactRouter?: boolean;
  hideMe: () => void;
  t: TranslateFunc;
}

function VerticalSlidingMenu({
  show = false,
  hideMe,
  useReactRouter = true,
  activeNavLinkIndex = 0,
  apps,
  navLinks,
  className,
  children,
  t,
}: PropsWithChildren<SlidingMenuProps>): React.ReactElement {
  const refSlidingMenu = useRef<HTMLDivElement>(null);
  const focusRef = useRef(null);
  const lastFocus = useRef<Element>();
  const selectMenuRef = useRef(null);
  const showAppSwitcher = Array.isArray(apps) && apps.length;
  const showNavLinks = Array.isArray(navLinks) && navLinks.length;
  const { height: windowHeight } = useWindowSize();

  const onFocusOut = (event) => onFocusOutAction(event, refSlidingMenu, focusRef, lastFocus);

  useEffect(() => {
    const onMouseDownListener = (e) => {
      if (refSlidingMenu.current && !refSlidingMenu.current.contains(e.target)) {
        hideMe();
      }
    };

    const onKeyPressListener = (event) => {
      if (!selectMenuRef.current) {
        onEscape(event, hideMe);
      }
    };

    const removeAllListeners = () => {
      document.removeEventListener(EventName.FOCUSOUT, onFocusOut, true);
      document.removeEventListener(EventName.MOUSEDOWN, onMouseDownListener);
      document.removeEventListener(EventName.KEYUP, onKeyPressListener);
      lastFocus.current = null; // forget last focus when menu is closed
    };

    if (show) {
      focusRef.current?.focus();
      document.addEventListener(EventName.FOCUSOUT, onFocusOut, true);
      document.addEventListener(EventName.MOUSEDOWN, onMouseDownListener);
      document.addEventListener(EventName.KEYUP, onKeyPressListener);
    } else {
      removeAllListeners();
    }
    return () => {
      removeAllListeners();
    };
  }, [show, hideMe]);

  const renderNavLinks = () => {
    const links = [];
    // eslint-disable-next-line no-plusplus
    if (showNavLinks) {
      for (let i = 0; i < navLinks.length; i += 1) {
        const navLink = navLinks[i];
        const isActive = i === activeNavLinkIndex;
        const ref = i === 0 ? focusRef : null;
        links.push(
          <NavItem key={navLink.name}>
            <KitNavLink
              tabIndex={show ? undefined : -1}
              useReactRouter={useReactRouter}
              isActive={isActive}
              name={navLink.name}
              path={navLink.path}
              onClick={navLink.onSelect}
              ref={ref}
              t={t}
            />
          </NavItem>
        );
      }
    }

    return <VerticalNav>{links}</VerticalNav>;
  };

  const renderAppsDropdown = () => {
    // Per specs we should hide the AppSwitcher if there's only 1 app. Just show a title here.
    if (apps.length === 1) {
      return <AppTitle>{apps[0].name}</AppTitle>;
    }

    // Treat an empty array as an error!
    if (apps.length === 0) {
      console.error('Invalid apps array');
      return null;
    }

    // Otherwise render an app switcher dropdown
    let defaultValue = {
      value: '',
      label: '',
    };
    const dropdownItems = [];
    for (let i = 0; i < apps.length; i += 1) {
      if (window.location.origin === apps[i].url) {
        defaultValue = {
          value: apps[i].url,
          label: apps[i].name,
        };
      }
      dropdownItems.push({
        value: apps[i].url,
        label: apps[i].name,
      });
    }

    const handleChange = (event) => {
      if (event.value !== window.location.origin) {
        window.location.href = event.value;
      }
    };

    const onClose = () => {
      setTimeout(() => {
        selectMenuRef.current = false;
      }, 150);
    };

    return (
      <AppSwitcher
        defaultValue={defaultValue}
        options={dropdownItems}
        onChange={handleChange}
        onMenuOpen={() => {
          selectMenuRef.current = true;
        }}
        onMenuClose={onClose}
        isSearchable={false}
        placeholder={defaultValue.label}
        aria-label={defaultValue.label}
        className='select2'
        classNamePrefix='select2'
        // For testing purposes
        id='kit-select-app-switcher-2'
      />
    );
  };

  return (
    <>
      <SlidingMenuTransparentBG isOpen={show} />
      <SlidingMenu
        className={className}
        ref={refSlidingMenu}
        aria-label={t('kit:nav_bar.aria.navigation_aside')}
        isOpen={show}
        height={windowHeight}
      >
        <SlidingMenuHeader>
          <img src={smallLogo} alt={t('kit:nav_bar.alt.header_logo')} />
        </SlidingMenuHeader>
        {showAppSwitcher && <SlidingMenuAppsList>{renderAppsDropdown()}</SlidingMenuAppsList>}
        <SlidingMenuBody>
          {renderNavLinks()}
          <StyledHr />
          <ListGroup>{children}</ListGroup>
        </SlidingMenuBody>
      </SlidingMenu>
    </>
  );
}

export default VerticalSlidingMenu;
